import nlDict from './nl.json';
import enDict from './en.json';

export type Locale = 'nl' | 'en';
export type TranslationKey = keyof typeof nlDict & keyof typeof enDict;
type Dict = Record<string, string>;

const dictionaries: Record<Locale, Dict> = {
  nl: nlDict as Dict,
  en: enDict as Dict,
};

export const SUPPORTED_LOCALES: readonly Locale[] = ['nl', 'en'];

export function isLocale(value: unknown): value is Locale {
  return value === 'nl' || value === 'en';
}

/**
 * Look up a translation for `key` in the given locale.
 *
 * - If the key is missing, returns the key itself and warns in development.
 * - `params` are interpolated into `{name}` placeholders in the resolved
 *   string.
 */
export function t(locale: Locale, key: string, params?: Record<string, string | number>): string {
  const dict = dictionaries[locale];
  const raw = dict[key];

  if (raw === undefined) {
    if (import.meta.env.DEV) {
      console.warn(`[i18n] Missing key "${key}" for locale "${locale}"`);
    }
    return key;
  }

  if (params === undefined) return raw;
  return interpolate(raw, params);
}

function interpolate(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

/**
 * Resolve the default locale: stored preference (if any) → navigator.language
 * if it starts with `nl` or `en` → fallback `en`.
 */
export function resolveDefaultLocale(
  stored: unknown,
  navigatorLanguage: string | undefined,
): Locale {
  if (isLocale(stored)) return stored;
  if (navigatorLanguage) {
    const lower = navigatorLanguage.toLowerCase();
    if (lower.startsWith('nl')) return 'nl';
    if (lower.startsWith('en')) return 'en';
  }
  return 'en';
}
